/**
 * Shared lead-capture facade (architecture §2).
 *
 * Single path: POST to the backend contact endpoint (`submitLead`), which
 * creates an ERPNext Lead (system of record) tagged with `source` and sends a
 * notification email. There is NO `mailto:` fallback — the inbox address never
 * reaches the client, and the visitor's email client is never opened. On a
 * backend failure the caller's catch block shows a graceful inline error and
 * the user can retry.
 */

import type { ContactFormResponse, LeadCaptureInput } from '../types/contact';
import type { MonetisationLeadRecord } from '../types/monetisation';
import { submitLead, submitMonetisationLead } from './contactApi';

export interface LeadCaptureResult {
  success: boolean;
  message: string;
}

export async function captureLead(input: LeadCaptureInput): Promise<LeadCaptureResult> {
  const response: ContactFormResponse = await submitLead(input);
  return { success: response.success, message: response.message };
}

/**
 * Captures a monetisation lead from the export intent gate through the shared
 * lead-capture path (AC fx-2.2#AC3). Creates an ERPNext Lead tagged
 * `reverseTax-b2b` via the backend; the monetisation dimensions + calculation
 * context are serialized into the message body.
 */
export async function captureMonetisationLead(
  record: MonetisationLeadRecord,
): Promise<LeadCaptureResult> {
  const response: ContactFormResponse = await submitMonetisationLead(record);
  return { success: response.success, message: response.message };
}
